import { create } from 'zustand';
const useStreamrAppOutput = create(() => ({
    ready: false,
    isVideoStreaming: false,
    audioMuted: true,
    watchingStartedAt: undefined,
    broadcastedVideoQuality: 'sd', // TODO: MULTIBROADCASTER - add handling of this when implementing the broadcasting in StreamrAppService
    activePublishers: [],
    activeVideoTracks: [],
    activeAudioTracks: [],
    filmStripGroups: [],
    thumbnailCanvasKeys: {},
    sessionMode: 'broadcast',
    broadcastingEnabled: true,
    broadcastMetadataReceived: false,
    localIsSpeaking: false,
    dominantSpeakerUserId: null,
    remoteSpeechStates: {},
    lowEndConferencingActive: false,
    lowEndParticipants: [],
    directVideoTrack: null,
    audioContextBlocked: false,
    userMutedAudio: false,
}));
const reactiveOutput = {
    useReady: () => {
        return useStreamrAppOutput((s) => s.ready);
    },
    useIsVideoStreaming: () => {
        return useStreamrAppOutput((s) => s.isVideoStreaming);
    },
    useAudioMuted: () => {
        return useStreamrAppOutput((s) => s.audioMuted);
    },
    useWatchingStartedAt: () => {
        return useStreamrAppOutput((s) => s.watchingStartedAt);
    },
    useBroadcastedVideoQuality: () => {
        return useStreamrAppOutput((s) => s.broadcastedVideoQuality);
    },
    useActivePublishers: () => {
        return useStreamrAppOutput((s) => s.activePublishers);
    },
    useActiveVideoTracks: () => {
        return useStreamrAppOutput((s) => s.activeVideoTracks);
    },
    useActiveAudioTracks: () => {
        return useStreamrAppOutput((s) => s.activeAudioTracks);
    },
    useFilmStripGroups: () => {
        return useStreamrAppOutput((s) => s.filmStripGroups);
    },
    useThumbnailCanvasKeys: () => {
        return useStreamrAppOutput((s) => s.thumbnailCanvasKeys);
    },
    useSessionMode: () => {
        return useStreamrAppOutput((s) => s.sessionMode);
    },
    useBroadcastingEnabled: () => {
        return useStreamrAppOutput((s) => s.broadcastingEnabled);
    },
    useBroadcastMetadataReceived: () => {
        return useStreamrAppOutput((s) => s.broadcastMetadataReceived);
    },
    useLocalIsSpeaking: () => {
        return useStreamrAppOutput((s) => s.localIsSpeaking);
    },
    useDominantSpeakerUserId: () => {
        return useStreamrAppOutput((s) => s.dominantSpeakerUserId);
    },
    useRemoteSpeechStates: () => {
        return useStreamrAppOutput((s) => s.remoteSpeechStates);
    },
    useLowEndConferencingActive: () => {
        return useStreamrAppOutput((s) => s.lowEndConferencingActive);
    },
    useLowEndParticipants: () => {
        return useStreamrAppOutput((s) => s.lowEndParticipants);
    },
    useDirectVideoTrack: () => {
        return useStreamrAppOutput((s) => s.directVideoTrack);
    },
    useAudioContextBlocked: () => {
        return useStreamrAppOutput((s) => s.audioContextBlocked);
    },
    useUserMutedAudio: () => {
        return useStreamrAppOutput((s) => s.userMutedAudio);
    },
};
export class StreamrAppServiceOutput {
    static getReactiveOutput() {
        return reactiveOutput;
    }
    setReady(ready) {
        useStreamrAppOutput.setState({ ready });
    }
    setIsVideoStreaming(isVideoStreaming) {
        useStreamrAppOutput.setState({ isVideoStreaming });
    }
    setAudioMuted(audioMuted) {
        useStreamrAppOutput.setState({ audioMuted });
    }
    getAudioMuted() {
        return useStreamrAppOutput.getState().audioMuted;
    }
    setWatchingStartedAt(watchingStartedAt) {
        useStreamrAppOutput.setState({ watchingStartedAt });
    }
    getWatchingStartedAt() {
        return useStreamrAppOutput.getState().watchingStartedAt;
    }
    setActivePublishers(activePublishers) {
        useStreamrAppOutput.setState({ activePublishers });
    }
    setActiveVideoTracks(tracks) {
        useStreamrAppOutput.setState({ activeVideoTracks: tracks });
    }
    setActiveAudioTracks(tracks) {
        useStreamrAppOutput.setState({ activeAudioTracks: tracks });
    }
    setFilmStripGroups(groups) {
        useStreamrAppOutput.setState({ filmStripGroups: groups });
    }
    rekeyThumbnailCanvas(canvasId) {
        useStreamrAppOutput.setState((s) => ({
            thumbnailCanvasKeys: {
                ...s.thumbnailCanvasKeys,
                [canvasId]: (s.thumbnailCanvasKeys[canvasId] || 0) + 1,
            },
        }));
    }
    setSessionMode(sessionMode) {
        useStreamrAppOutput.setState({ sessionMode });
    }
    getSessionMode() {
        return useStreamrAppOutput.getState().sessionMode;
    }
    setBroadcastingEnabled(broadcastingEnabled) {
        useStreamrAppOutput.setState({ broadcastingEnabled });
    }
    getBroadcastingEnabled() {
        return useStreamrAppOutput.getState().broadcastingEnabled;
    }
    setBroadcastMetadataReceived(received) {
        useStreamrAppOutput.setState({ broadcastMetadataReceived: received });
    }
    setLocalIsSpeaking(localIsSpeaking) {
        useStreamrAppOutput.setState({ localIsSpeaking });
    }
    setDominantSpeakerUserId(dominantSpeakerUserId) {
        useStreamrAppOutput.setState({ dominantSpeakerUserId });
    }
    getDominantSpeakerUserId() {
        return useStreamrAppOutput.getState().dominantSpeakerUserId;
    }
    setRemoteSpeechState(userId, state) {
        useStreamrAppOutput.setState((s) => ({
            remoteSpeechStates: {
                ...s.remoteSpeechStates,
                [userId]: state,
            },
        }));
    }
    getRemoteSpeechStates() {
        return useStreamrAppOutput.getState().remoteSpeechStates;
    }
    removeRemoteSpeechState(userId) {
        useStreamrAppOutput.setState((s) => {
            const { [userId]: _, ...rest } = s.remoteSpeechStates;
            return { remoteSpeechStates: rest };
        });
    }
    setLowEndConferencingActive(active) {
        useStreamrAppOutput.setState({ lowEndConferencingActive: active });
    }
    getLowEndConferencingActive() {
        return useStreamrAppOutput.getState().lowEndConferencingActive;
    }
    setLowEndParticipants(participants) {
        useStreamrAppOutput.setState({ lowEndParticipants: participants });
    }
    setDirectVideoTrack(track) {
        useStreamrAppOutput.setState({ directVideoTrack: track });
    }
    getDirectVideoTrack() {
        return useStreamrAppOutput.getState().directVideoTrack;
    }
    setAudioContextBlocked(blocked) {
        useStreamrAppOutput.setState({ audioContextBlocked: blocked });
    }
    setUserMutedAudio(userMuted) {
        useStreamrAppOutput.setState({ userMutedAudio: userMuted });
    }
    getUserMutedAudio() {
        return useStreamrAppOutput.getState().userMutedAudio;
    }
}
